import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';

import { TranslatePipe } from '@core/i18n-legacy';
import type { TimelineEvent } from '@core/tellent-assistant-util';
import { ButtonComponent, IconComponent } from '@core/user-interface-legacy';

import { TellentAssistantToolPillComponent } from '../tool-pill/tool-pill.component';

export type ThinkingRunState = 'running' | 'done';

@Component({
  selector: 'tellent-assistant-thinking-section',
  imports: [ButtonComponent, IconComponent, TellentAssistantToolPillComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './thinking-section.component.html',
  styleUrl: './thinking-section.component.less',
})
export class TellentAssistantThinkingSectionComponent {
  protected readonly userExpanded = signal<boolean | null>(null);

  protected readonly toolEvents = computed(() =>
    this.timeline().filter(
      (event): event is Extract<TimelineEvent, { kind: 'tool' }> => event.kind === 'tool',
    ),
  );

  protected readonly stepCount = computed(() => this.toolEvents().length);

  protected readonly currentToolName = computed<string | null>(() => {
    if (this.runState() !== 'running') return null;
    const tools = this.toolEvents();
    const last = tools[tools.length - 1];
    if (!last || last.hasResult) return null;
    return last.name;
  });

  protected readonly hasContent = computed(() => this.timeline().length > 0);

  protected readonly expanded = computed(() => {
    const override = this.userExpanded();
    if (override !== null) return override;
    return this.runState() === 'running';
  });

  // Reset user override whenever the run flips back to running so the next
  // run starts in its natural auto-expanded state.
  protected readonly resetOverrideOnRunStart = effect(() => {
    if (this.runState() === 'running') this.userExpanded.set(null);
  });

  public readonly timeline = input.required<readonly TimelineEvent[]>();
  public readonly runState = input.required<ThinkingRunState>();

  protected toggle(): void {
    this.userExpanded.set(!this.expanded());
  }
}
